/**
 * Tenant Guards
 *
 * Guards fuer Multi-Tenant Zugriffskontrolle basierend auf TenantContextService.
 *
 * Guards:
 * - tenantGuard: Prueft ob User einem Tenant zugeordnet ist
 * - locationGuard: Prueft ob User eine Location ausgewaehlt hat
 * - tenantAdminGuard: Prueft ob User Admin-Rechte im Tenant hat
 *
 * @author TypeScript Developer
 * @version 1.0.0
 */

import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { map, take, filter } from 'rxjs/operators';
import { TenantContextService } from '../services/tenant-context.service';
import { AuthStateService } from '../services/auth-state.service';

/**
 * Guard: Prueft ob User einem Tenant zugeordnet ist
 *
 * ✅ ENTERPRISE FIX: Nutzt nur TenantContext statt getIdTokenResult()
 * Verhindert Race Conditions und Claims-Reloads bei jeder Navigation
 *
 * Verwendet:
 * - Bei allen Routes die Tenant-Context benoetigen
 * - Redirectet zu /onboarding wenn kein Tenant
 * - SuperAdmins werden über Permissions geprüft (aus TenantContext)
 *
 * @example
 * ```typescript
 * {
 *   path: 'dashboard',
 *   canActivate: [tenantGuard],
 *   component: DashboardComponent
 * }
 * ```
 */
export const tenantGuard: CanActivateFn = (route, state) => {
  const tenantContext = inject(TenantContextService);
  const router = inject(Router);

  const tenantId = tenantContext.tenantId();

  // Wenn tenantId vorhanden -> durchlassen
  if (tenantId) {
    return true;
  }

  // Kein tenantId -> onboarding
  // (SuperAdmins werden durch andere Guards behandelt)
  console.warn('[TenantGuard] No tenant context - redirecting to onboarding');
  return router.createUrlTree(['/onboarding']);
};

/**
 * Guard: Prueft ob User eine Location ausgewaehlt hat
 *
 * Verwendet:
 * - Bei Routes die Location-spezifische Daten benoetigen
 * - Kombiniert mit tenantGuard
 * - Redirectet zu /select-location wenn keine Location
 *
 * @example
 * ```typescript
 * {
 *   path: 'checkout',
 *   canActivate: [tenantGuard, locationGuard],
 *   component: CheckoutComponent
 * }
 * ```
 */
export const locationGuard: CanActivateFn = (route, state) => {
  const tenantContext = inject(TenantContextService);
  const router = inject(Router);

  const tenantId = tenantContext.tenantId();
  const locationId = tenantContext.locationId();

  if (!tenantId) {
    console.warn('[LocationGuard] No tenant context - redirecting to onboarding');
    return router.createUrlTree(['/onboarding']);
  }

  if (!locationId) {
    console.warn('[LocationGuard] No location selected - redirecting to select-location');
    return router.createUrlTree(['/select-location']);
  }

  return true;
};

/**
 * Guard: Prueft ob User Admin-Rechte im Tenant hat
 *
 * ✅ ENTERPRISE v2.0: AuthService updated TenantContext ATOMAR vor setInitialized(true)
 * Race Condition ist durch atomare State-Updates im AuthService gelöst!
 *
 * WICHTIG: Dieser Guard kombiniert tenantGuard + tenantAdminGuard
 * → Kein separater tenantGuard in canActivate notwendig!
 *
 * Erlaubt:
 * - Owner (role === 'owner')
 * - Admin (role === 'admin')
 * - Manager mit entsprechenden Permissions
 *
 * Verwendet:
 * - Bei Admin-only Routes (Settings, User-Management, etc.)
 * - Ersetzt [tenantGuard, tenantAdminGuard] durch nur [tenantAdminGuard]
 * - Redirectet zu /onboarding wenn kein Tenant
 * - Redirectet zu /unauthorized wenn keine Admin-Rechte
 *
 * @example
 * ```typescript
 * {
 *   path: 'settings',
 *   canActivate: [tenantAdminGuard], // tenantGuard ist integriert!
 *   component: SettingsComponent
 * }
 * ```
 */
export const tenantAdminGuard: CanActivateFn = (route, state) => {
  const authStateService = inject(AuthStateService);
  const tenantContext = inject(TenantContextService);
  const router = inject(Router);

  // ✅ ENTERPRISE v2.0: Warte auf Auth-Initialisierung
  // AuthService garantiert: Wenn initialized=true, dann ist TenantContext GARANTIERT gesetzt!
  // (durch atomare State-Updates im AuthService)
  return authStateService.authInitialized$.pipe(
    filter(initialized => initialized === true), // Warte bis Auth vollständig geladen
    take(1), // Observable completion (Best Practice für Guards)
    map(() => {
      // ✅ Auth ist initialisiert - TenantContext ist GARANTIERT ready
      const tenantId = tenantContext.tenantId();

      console.log('[TenantAdminGuard] Auth initialized - checking tenant:', {
        tenantId,
        role: tenantContext.role()
      });

      // CHECK 1: Tenant vorhanden? (tenantGuard Logik integriert)
      if (!tenantId) {
        console.warn('[TenantAdminGuard] No tenant - redirecting to onboarding');
        return router.createUrlTree(['/onboarding']);
      }

      // CHECK 2: Admin-Rechte vorhanden? (tenantAdminGuard Logik)
      const isAdmin = tenantContext.isOwner() || tenantContext.isAdmin();
      if (isAdmin) {
        console.log('[TenantAdminGuard] ✅ Admin access granted - role:', tenantContext.role());
        return true; // Admin-Zugriff erlaubt
      }

      // Keine Admin-Rechte → unauthorized
      console.warn('[TenantAdminGuard] Admin access required - redirecting to unauthorized');
      return router.createUrlTree(['/unauthorized']);
    })
  );
};
